import React from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Code, Eye, Minus, Plus as PlusIcon, UploadCloud } from 'lucide-react';

export const EXERCISE_TYPES = [
  { id: 'custom', label: 'Custom exercise', sub: 'Write your own task to assess real-world skills.' },
  { id: 'coding', label: 'Coding exercise', sub: 'Solve coding problems or fix faulty code with support from an embedded AI-enabled IDE.' },
  { id: 'human-data', label: 'Human Data exercise', sub: 'After conversational interview there will be a 25 mins data annotation exercise.' },
  { id: 'speech-to-text', label: 'Speech to text exercise', sub: 'Simulate real-world tasks like voice transcription, data annotation labelling, or reasoning tasks.' },
  { id: 'mcq', label: 'Multiple choice test', sub: 'Multiple-choice questions to test candidate knowledge, either AI generated or manual.' },
  { id: 'big-five', label: 'Big five personality assessment', sub: 'This environment explores how candidates score across the Big Five personality traits.' },
  { id: 'on-the-fly', label: 'On the fly exercise', sub: 'Describe the task and Zara will guide you step-by-step to create the perfect exercise.', beta: true },
];

export function RadioRow({ t, selected, onSelect }) {
  const active = selected === t.id;
  return (
    <button
      onClick={() => onSelect(t.id)}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
        active ? 'border-[#5B4FE8] bg-[#F5F1FE]' : 'border-[#ECE6FA] bg-white hover:border-[#C7BFF5]'
      }`}
    >
      <span
        className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 shrink-0 ${
          active ? 'border-[#5B4FE8]' : 'border-[#C4BEDA]'
        }`}
      >
        {active && <span className="w-2 h-2 rounded-full bg-[#5B4FE8]" />}
      </span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-semibold text-[#1A1533]">{t.label}</span>
          {t.beta && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E9E3FB] text-[#5B4FE8]">Beta</span>
          )}
        </div>
        <p className="text-[12px] text-[#4A4463] mt-0.5 leading-relaxed">{t.sub}</p>
      </div>
    </button>
  );
}

export function RichTextToolbar() {
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 rounded-t-lg border border-b-0 border-[#ECE6FA] bg-white">
      <select className="text-[12px] border-0 outline-none text-[#1A1533] bg-transparent font-semibold">
        <option>Normal</option>
        <option>Heading 1</option>
        <option>Heading 2</option>
      </select>
      <div className="w-px h-4 bg-[#ECE6FA] mx-1" />
      {[Bold, Italic, Underline, Code, List, ListOrdered].map((Ic, i) => (
        <button key={`t-${i}`} className="w-7 h-7 rounded hover:bg-[#F1ECFE] text-[#4A4463] flex items-center justify-center">
          <Ic size={13} />
        </button>
      ))}
    </div>
  );
}

export function UploadButton() {
  return (
    <>
      <div className="mt-4 text-center text-[12px] text-[#8A83A5]">or</div>
      <button className="mt-3 w-full py-5 rounded-xl border-2 border-dashed border-[#C7BFF5] bg-[#FAF8FF] text-[#5B4FE8] text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#F5F1FE]">
        <UploadCloud size={16} />
        Click to upload or drag & drop (.pdf)
      </button>
    </>
  );
}

export function AnswerAndDuration({ module, onChange, hideAnswerMode }) {
  const duration = module.duration ?? 30;
  const answerMode = module.answerMode || 'text';
  const setDuration = (d) => onChange({ duration: Math.max(1, Math.min(180, d)) });

  return (
    <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
      {!hideAnswerMode && (
        <div className="flex items-center gap-4">
          <span className="text-[12.5px] text-[#4A4463]">Candidate answers with:</span>
          <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1A1533]">
            <input type="radio" checked={answerMode === 'audio'} onChange={() => onChange({ answerMode: 'audio' })} className="accent-[#5B4FE8]" />
            Audio
          </label>
          <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1A1533]">
            <input type="radio" checked={answerMode === 'text'} onChange={() => onChange({ answerMode: 'text' })} className="accent-[#5B4FE8]" />
            Text
          </label>
        </div>
      )}

      <div className="flex items-center gap-3 ml-auto">
        <span className="text-[12.5px] text-[#4A4463]">Define time (in minutes)</span>
        <div className="inline-flex items-center rounded-lg border border-[#ECE6FA] bg-white">
          <button onClick={() => setDuration(duration - 1)} className="w-8 h-8 flex items-center justify-center text-[#5B4FE8] hover:bg-[#F5F1FE] rounded-l-lg">
            <Minus size={14} />
          </button>
          <input
            type="number"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-14 text-center text-[13px] font-semibold outline-none bg-transparent"
          />
          <button onClick={() => setDuration(duration + 1)} className="w-8 h-8 flex items-center justify-center text-[#5B4FE8] hover:bg-[#F5F1FE] rounded-r-lg">
            <PlusIcon size={14} />
          </button>
        </div>
      </div>

      <button className="text-[12.5px] text-[#5B4FE8] font-semibold hover:underline inline-flex items-center gap-1">
        <Eye size={13} /> View exercise
      </button>
    </div>
  );
}
